import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  TextField,
  ReferenceField,
  ShowButton,
  NumberField,
  ListBase,
  ListToolbar,
  Pagination,
  FunctionField,
  useRefresh,
  Title,
  useListContext,
  Datagrid,
  DateField,
  ListContextProvider,
} from 'react-admin';
import { useHistory } from 'react-router-dom';

import { Card, Divider, Tabs, Tab } from '@material-ui/core';
import { stringify } from 'query-string';

import { DialogoCliente2, DialogoConfirmacao } from '../../Components';
import { api } from '../../services';
import {
  STATUS_ATIVO,
  STATUS_INATIVO,
  STATUS_DELETADO,
} from '../../utils/constants';
import ActionsField from './ActionsField';
import Actions from './ActionsList';
import DialogoResetarSenha from './DialogoResetarSenha';
import Filter from './Filter';

const tabs = [
  { id: 'ativos', name: 'Ativos' },
  { id: 'inativos', name: 'Inativos' },
];

const MyList = ({ ...props }) => {
  const refDialogoCompra = useRef(null);
  const refresh = useRefresh();
  const listContext = useListContext();
  const { ids, filterValues, setFilters, displayedFilters } = listContext;
  const history = useHistory();
  const refDialogoConfirmacao = useRef(null);
  const [ativos, setAtivos] = useState([]);
  const [inativos, setInativos] = useState([]);
  const refDialogoResetarSenha = useRef(null);

  function handleCriar() {
    if (refDialogoCompra.current) {
      refDialogoCompra.current.handleCreate(1);
    }
  }

  function handleEdit({ id }) {
    if (refDialogoCompra.current) {
      refDialogoCompra.current.handleEdit(id);
    }
  }

  function handleShow({ id }) {
    history.push(`/clientes/${id}/show`);
  }

  function handleAtivar({ id }) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'Reativação de Cliente',
        'Deseja realmente reativar este cliente?',
        { id, codigo: 1 },
      );
    }
  }

  function handleDesativar({ id }) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'Desativação de Cliente',
        'Deseja realmente desativar este cliente?',
        { id, codigo: 2 },
      );
    }
  }

  function handleDeletar({ id }) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'Exclusão de Cliente',
        'Deseja deletar permanentemente este cliente?',
        { id, codigo: 3 },
      );
    }
  }

  async function ativar(id) {
    await api.put(`/clientes/status/ativar/${id}`);
    handleClose();
  }

  async function desativar(id) {
    await api.put(`/clientes/status/inativar/${id}`);
    handleClose();
  }

  async function deletar(id) {
    await api.put(`/clientes/status/deletar/${id}`);
    handleClose();
  }

  async function handleConfirma({ id, codigo }) {
    switch (codigo) {
      case 1:
        ativar(id);
        break;
      case 2:
        desativar(id);
        break;
      case 3:
        deletar(id);
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    if (ids && ids !== filterValues.status) {
      switch (filterValues.status) {
        case STATUS_ATIVO:
          setAtivos(ids);
          break;
        case STATUS_INATIVO:
          setInativos(ids);
          break;
        default:
          break;
      }
    }
  }, [ids, filterValues.status]);

  const handleChange = useCallback(
    (event, value) => {
      if (setFilters) {
        setFilters(
          {
            ...filterValues,
            status: value === 'ativos' ? STATUS_ATIVO : STATUS_INATIVO,
          },
          displayedFilters,
        );
      }
    },
    [displayedFilters, filterValues, setFilters],
  );

  const handleClose = () => {
    refresh();
  };

  function handleResetarSenha(record) {
    if (refDialogoResetarSenha.current) {
      refDialogoResetarSenha.current.handleOpen(record.id);
    }
  }

  function handleMercadoriaPendente(record) {
    history.push(`/resumo-mercadorias-cliente?cliente_id=${record.id}`);
  }

  function handleVendasParciaisPendentes({ id }) {
    const queryOj = {};

    queryOj.displayedFilters = JSON.stringify({
      cliente_id: true,
      is_em_partes: true,
      status_entrega: true,
    });
    queryOj.filter = JSON.stringify({
      cliente_id: id,
      is_em_partes: true,
      status_entrega: 'INCOMPLETO',
      cancelado: false,
    });

    history.push(
      `/vendas?${stringify(queryOj)}&order=DESC&page=1&perPage=50&sort=data`,
    );
  }

  function getClassificacao(score) {
    if (score < 100) return 'Cobre';
    if (score < 200) return 'Prata';
    if (score < 300) return 'Platina';
    if (score < 400) return 'Ouro';
    if (score < 500) return 'Ródio';
    if (score < 600) return 'Irídio';
    if (score < 700) return 'Paládio';
    if (score < 800) return 'Jadeíta';
    if (score < 900) return 'Plutônio';
    if (score < 1000) return 'Musgravite';
    if (score < 2000) return 'Painita';
    if (score < 3000) return 'Alexandrita';
    if (score < 4000) return 'Esmeralda';
    if (score < 5000) return 'Ruby';
    if (score < 10000) return 'Taaffeite';
    if (score < 20000) return 'Trítio';
    if (score < 30000) return 'Diamante';
    if (score < 40000) return 'Diamante Azul';
    if (score < 50000) return 'Diamante Rosa';
    if (score < 100000) return 'Califórnio';
    if (score < 200000) return 'Antimatéria';
  }

  return (
    <>
      <Title title="Clientes" />
      <ListToolbar
        filters={props.filters}
        actions={<Actions handleCriar={handleCriar} {...props} />}
        {...props}
      />
      <Card style={{ borderRadius: '20px' }} {...props}>
        <Divider />
        <Tabs
          variant="fullWidth"
          centered
          value={filterValues.status === STATUS_ATIVO ? tabs[0].id : tabs[1].id}
          indicatorColor="primary"
          onChange={handleChange}
        >
          {tabs.map((choice) => (
            <Tab key={choice.id} label={choice.name} value={choice.id} />
          ))}
        </Tabs>
        <Divider />
        <div>
          {filterValues.status === STATUS_ATIVO && (
            <ListContextProvider value={{ ...listContext, ids: ativos }}>
              <Datagrid {...props}>
                <TextField source="id" />
                <TextField source="nome" label="Nome" />
                <TextField source="nome_fantasia" label="Nome Fantasia" />
                <TextField source="razao_social" label="Razão Social" />
                <ReferenceField
                  source="cidade_id"
                  reference="cidades"
                  label="Cidade"
                  sortable={false}
                >
                  <TextField source="nome" />
                </ReferenceField>
                <TextField source="bairro" label="Bairro" />
                <DateField source="dataAniversario" label="Aniversário" />
                <DateField source="created_at" label="Data Cadastramento" />
                <ReferenceField
                  source="user_id"
                  reference="users"
                  label="Adm"
                  allowEmpty={false}
                  sortable={false}
                >
                  <TextField source="username" />
                </ReferenceField>
                <FunctionField
                  source="score_total"
                  label="Score"
                  render={(record) =>
                    !record.is_fidelidade ? '-' : record.score_total
                  }
                />
                <FunctionField
                  label="Créditos não usados"
                  render={(record) =>
                    !record.is_fidelidade ? '-' : record.score_nao_usado
                  }
                />
                <FunctionField
                  label="Classificação"
                  render={(record) =>
                    !record.is_fidelidade
                      ? '-'
                      : getClassificacao(record.score_total)
                  }
                />
                <ActionsField
                  source="valor"
                  textAlign="right"
                  label="Ações"
                  sortable={false}
                  handleEdit={handleEdit}
                  handleShow={handleShow}
                  handleResetarSenha={handleResetarSenha}
                  handleMercadoriaPendente={handleMercadoriaPendente}
                  handleVendasParciaisPendentes={handleVendasParciaisPendentes}
                  handleAtivar={handleAtivar}
                  handleDesativar={handleDesativar}
                  handleDeletar={handleDeletar}
                />
              </Datagrid>
            </ListContextProvider>
          )}
          {filterValues.status === STATUS_INATIVO && (
            <ListContextProvider value={{ ...listContext, ids: inativos }}>
              <Datagrid {...props}>
                <TextField source="id" />
                <TextField source="nome" label="Nome" />
                <TextField source="nome_fantasia" label="Nome Fantasia" />
                <TextField source="razao_social" label="Razão Social" />
                <ReferenceField
                  source="cidade_id"
                  reference="cidades"
                  label="Cidade"
                  sortable={false}
                >
                  <TextField source="nome" />
                </ReferenceField>
                <TextField source="bairro" label="Bairro" />
                <DateField source="dataAniversario" label="Aniversário" />
                <DateField source="created_at" label="Data Cadastramento" />
                <ReferenceField
                  source="user_id"
                  reference="users"
                  label="Adm"
                  allowEmpty={false}
                  sortable={false}
                >
                  <TextField source="username" />
                </ReferenceField>
                <FunctionField
                  source="score_total"
                  label="Score"
                  render={(record) =>
                    !record.is_fidelidade ? '-' : record.score_total
                  }
                />
                <FunctionField
                  label="Créditos não usados"
                  render={(record) =>
                    !record.is_fidelidade ? '-' : record.score_nao_usado
                  }
                />
                <FunctionField
                  label="Classificação"
                  render={(record) =>
                    !record.is_fidelidade
                      ? '-'
                      : getClassificacao(record.score_total)
                  }
                />
                <ActionsField
                  source="valor"
                  textAlign="right"
                  label="Ações"
                  sortable={false}
                  handleEdit={handleEdit}
                  handleShow={handleShow}
                  handleResetarSenha={handleResetarSenha}
                  handleMercadoriaPendente={handleMercadoriaPendente}
                  handleVendasParciaisPendentes={handleVendasParciaisPendentes}
                  handleAtivar={handleAtivar}
                  handleDesativar={handleDesativar}
                  handleDeletar={handleDeletar}
                />
              </Datagrid>
            </ListContextProvider>
          )}
        </div>
        <TransacoesPagination />
      </Card>
      <DialogoCliente2 ref={refDialogoCompra} handleClose={handleClose} />
      <DialogoResetarSenha
        ref={refDialogoResetarSenha}
        handleClose={handleClose}
      />
      <DialogoConfirmacao
        ref={refDialogoConfirmacao}
        handleConfirma={handleConfirma}
      />
    </>
  );
};

const ContainerMyList = ({ children, ...props }) => (
  <>
    <ListBase {...props}>
      <MyList {...props} />
    </ListBase>
  </>
);

const TransacoesPagination = (props) => (
  <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />
);

const List = (props) => (
  <>
    <ContainerMyList
      {...props}
      bulkActionButtons={false}
      pagination={<TransacoesPagination />}
      perPage={50}
      filters={<Filter />}
      title="Clientes"
      filterDefaultValues={{ status: STATUS_ATIVO }}
      component="div"
    />
  </>
);

export default List;
